import { useEffect, useState } from 'react'
import { RELAY_PFAD } from '../config'

/**
 * Diagnoseseite fuer den Wohnzimmer-Fernseher.
 *
 * Smart-TV-Browser haengen haeufig auf Chromium-Staenden von 2017 bis 2019
 * fest. Statt zu raten, welche Fassung dort laeuft, ruft man diese Seite
 * einmal auf dem echten Geraet auf: Sie sagt in zwei Minuten, ob der
 * Fernseher mitspielt. Das steht am Anfang der Arbeit am TV-Modus und nicht
 * am Ende, weil eine weisse Seite auf dem Fernseher sonst erst auffaellt,
 * wenn alles gebaut ist.
 */
export function TvCheck() {
  const [relay, setRelay] = useState<{ ok: boolean; ms: number | null; text: string }>({
    ok: false,
    ms: null,
    text: 'prüfe …',
  })
  const [bildrate, setBildrate] = useState<number | null>(null)

  useEffect(() => {
    const start = Date.now()
    let sockel: WebSocket | null = null
    try {
      const protokoll = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      sockel = new WebSocket(`${protokoll}//${window.location.host}${RELAY_PFAD}`)
      sockel.onopen = () => {
        sockel?.send(JSON.stringify({ typ: 'ping' }))
      }
      sockel.onmessage = () => {
        setRelay({ ok: true, ms: Date.now() - start, text: 'verbunden' })
        sockel?.close()
      }
      sockel.onerror = () => setRelay({ ok: false, ms: null, text: 'keine Verbindung' })
    } catch {
      setRelay({ ok: false, ms: null, text: 'WebSocket fehlt' })
    }
    return () => sockel?.close()
  }, [])

  useEffect(() => {
    let bilder = 0
    let laeuft = true
    const start = performance.now()
    function zaehlen() {
      if (!laeuft) return
      bilder++
      if (performance.now() - start >= 1000) {
        setBildrate(Math.round((bilder * 1000) / (performance.now() - start)))
        laeuft = false
        return
      }
      requestAnimationFrame(zaehlen)
    }
    requestAnimationFrame(zaehlen)
    return () => {
      laeuft = false
    }
  }, [])

  const pruefungen = [
    ['CSS-Variablen', pruefeCss('--x', 'color', 'var(--x, red)')],
    ['CSS Grid', typeof CSS !== 'undefined' && CSS.supports?.('display', 'grid')],
    ['Flexbox', typeof CSS !== 'undefined' && CSS.supports?.('display', 'flex')],
    ['Einheit vmin', typeof CSS !== 'undefined' && CSS.supports?.('width', '10vmin')],
    ['WebSocket', typeof WebSocket !== 'undefined'],
    ['IndexedDB', typeof indexedDB !== 'undefined'],
    ['localStorage', pruefeSpeicher()],
    ['Web Audio', typeof (window as never as { AudioContext?: unknown }).AudioContext !== 'undefined'],
    ['SVG', typeof SVGElement !== 'undefined'],
    ['Promise.finally (ES2018)', typeof Promise.prototype.finally === 'function'],
    ['Object.fromEntries (ES2019)', typeof Object.fromEntries === 'function'],
    ['Optional Chaining (ES2020)', pruefeSyntax('a?.b')],
    ['crypto.randomUUID', typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'],
  ] as const

  return (
    <div style={{ padding: '3vmin', overflow: 'auto', height: '100%', background: '#0d0827' }}>
      <h1 className="titel" style={{ fontSize: '6vmin' }}>
        Zahlenflug: Fernseher-Check
      </h1>

      <Abschnitt titel="Kann dieser Browser, was die App braucht?">
        <table style={{ width: '100%', fontSize: '2.6vmin', borderCollapse: 'collapse' }}>
          <tbody>
            {pruefungen.map(([name, ok]) => (
              <tr key={name}>
                <td style={{ padding: '0.8vmin 0' }}>{name}</td>
                <td style={{ textAlign: 'right', color: ok ? 'var(--gruen)' : 'var(--rot)' }}>
                  {ok ? 'ja' : 'NEIN'}
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ padding: '0.8vmin 0' }}>Verbindung zum Server</td>
              <td
                style={{ textAlign: 'right', color: relay.ok ? 'var(--gruen)' : 'var(--rot)' }}
              >
                {relay.text}
                {relay.ms !== null ? ` (${relay.ms} ms)` : ''}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '0.8vmin 0' }}>Bilder pro Sekunde</td>
              <td
                style={{
                  textAlign: 'right',
                  color: (bildrate ?? 0) >= 30 ? 'var(--gruen)' : 'var(--gold)',
                }}
              >
                {bildrate ?? 'messe …'}
              </td>
            </tr>
          </tbody>
        </table>
      </Abschnitt>

      <Abschnitt titel="Schriftgrößen: Welche Zeile kannst du vom Sofa aus lesen?">
        {[3, 5, 8, 12, 18].map((groesse) => (
          <div key={groesse} style={{ fontSize: `${groesse}vmin`, fontWeight: 800 }}>
            {groesse} vmin: 7 × 8 = 56
          </div>
        ))}
        <p style={{ fontSize: '2.4vmin', opacity: 0.7, marginTop: '1vmin' }}>
          Die Aufgabe erscheint im Spiel mit 22 vmin, die Ballonzahlen mit 9 vmin.
        </p>
      </Abschnitt>

      <Abschnitt titel="Farben: Sind alle Flächen klar unterscheidbar?">
        <div style={{ display: 'flex', gap: '2vmin', flexWrap: 'wrap' }}>
          {[
            ['Ballon 1', 'var(--ballon-1)'],
            ['Ballon 2', 'var(--ballon-2)'],
            ['Ballon 3', 'var(--ballon-3)'],
            ['Ballon 4', 'var(--ballon-4)'],
            ['Gold', 'var(--gold)'],
            ['Hintergrund', 'var(--nacht)'],
          ].map(([name, farbe]) => (
            <div
              key={name}
              style={{
                background: farbe,
                color: '#241228',
                padding: '2vmin 3vmin',
                borderRadius: '1.5vmin',
                fontWeight: 700,
                fontSize: '2.4vmin',
              }}
            >
              {name}
            </div>
          ))}
        </div>
      </Abschnitt>

      <Abschnitt titel="Bewegung: Läuft das flüssig oder ruckelt es?">
        <div style={{ display: 'flex', gap: '3vmin' }}>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="stern"
              style={{
                position: 'relative',
                width: '6vmin',
                height: '6vmin',
                background: 'var(--gold)',
                animation: `schweben ${2 + i * 0.3}s ease-in-out infinite`,
              }}
            />
          ))}
        </div>
      </Abschnitt>

      <Abschnitt titel="Browser-Kennung">
        <code style={{ fontSize: '2vmin', wordBreak: 'break-all', opacity: 0.8 }}>
          {navigator.userAgent}
        </code>
        <p style={{ fontSize: '2.4vmin', marginTop: '1vmin', opacity: 0.8 }}>
          Bildschirm: {window.innerWidth} × {window.innerHeight} Punkte, Pixelverhältnis{' '}
          {window.devicePixelRatio}
        </p>
      </Abschnitt>
    </div>
  )
}

function Abschnitt({ titel, children }: { titel: string; children: React.ReactNode }) {
  return (
    <section
      style={{
        margin: '3vmin 0',
        padding: '2.5vmin',
        background: 'rgba(255,248,231,0.07)',
        borderRadius: '2vmin',
      }}
    >
      <h2 style={{ fontSize: '3.2vmin', margin: '0 0 2vmin', color: 'var(--gold)' }}>{titel}</h2>
      {children}
    </section>
  )
}

function pruefeCss(variable: string, eigenschaft: string, wert: string): boolean {
  try {
    const el = document.createElement('div')
    el.style.setProperty(variable, 'blue')
    el.style.setProperty(eigenschaft, wert)
    return el.style.getPropertyValue(eigenschaft) !== ''
  } catch {
    return false
  }
}

function pruefeSpeicher(): boolean {
  try {
    window.localStorage.setItem('zahlenflug:test', '1')
    window.localStorage.removeItem('zahlenflug:test')
    return true
  } catch {
    return false
  }
}

function pruefeSyntax(ausdruck: string): boolean {
  try {
    // eslint-disable-next-line no-new-func
    new Function(`const a = {b: 1}; return ${ausdruck}`)()
    return true
  } catch {
    return false
  }
}
